import { useCallback, useEffect, useId, useRef, useState } from "react"
import { Alert, Box, Button, CircularProgress, List, ListItemButton, ListItemText, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useScheduledWorkflowDeliverables } from "resources/scheduled-workflows/hooks"
import { CollapsibleSection, SectionMoreButton, useSidePanelSurface } from "../chatbox/collapsible-section"
import { reportEntryLabel } from "./report-format"
import { viewerTimeZone } from "./schedule-format"

const MESSAGE_SX = { m: 1 } satisfies SxProps<Theme>
const EMPTY_SX = { px: 2, py: 1, color: "text.secondary", typography: "body2" } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>

const VIEWER_ZONE = viewerTimeZone()

export const REPORTS_VISIBLE_LIMIT = 5

export function ReportsSection({ onOpen }: { onOpen: (deliverableId: string) => void }) {
  const headingId = useId()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { data, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage } = useScheduledWorkflowDeliverables()
  const deliverables = data?.pages.flatMap((page) => page.deliverables) ?? []
  const [isShowingAll, setIsShowingAll] = useState(false)
  const hasOverflow = deliverables.length > REPORTS_VISIBLE_LIMIT
  const [hadOverflow, setHadOverflow] = useState(hasOverflow)
  if (hasOverflow !== hadOverflow) {
    setHadOverflow(hasOverflow)
    if (!hasOverflow) setIsShowingAll(false)
  }
  const visibleDeliverables = isShowingAll ? deliverables : deliverables.slice(0, REPORTS_VISIBLE_LIMIT)
  const hiddenCount = deliverables.length - visibleDeliverables.length
  const listId = `${headingId}-list`
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const prevIsFetchingNextPage = useRef(false)
  useEffect(() => {
    // When the last page fetch completes, the fetch button unmounts and focus falls to body.
    // Move focus to the Show more/less toggle, which is still mounted.
    if (prevIsFetchingNextPage.current && !isFetchingNextPage && !hasNextPage) {
      toggleRef.current?.focus()
    }
    prevIsFetchingNextPage.current = isFetchingNextPage
  }, [isFetchingNextPage, hasNextPage])
  const loadNextPage = useCallback(() => {
    setIsShowingAll(true)
    void fetchNextPage()
  }, [fetchNextPage])
  const toggleShowAll = useCallback(() => setIsShowingAll((showingAll) => !showingAll), [])
  const analytics = useAnalytics()
  const surface = useSidePanelSurface()
  const toggleCollapsed = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.THREAD_SECTION_TOGGLED, { section: "reports", collapsed: !isCollapsed, ...(surface == null ? {} : { surface }) })
    setIsCollapsed(!isCollapsed)
  }, [analytics, isCollapsed, surface])

  return (
    <CollapsibleSection
      headingId={headingId}
      label="Reports"
      count={data == null ? undefined : `${deliverables.length}${hasNextPage ? "+" : ""}`}
      isCollapsed={isCollapsed}
      onToggleCollapse={toggleCollapsed}
      testId="chatbox-reports-section"
    >
      {isError && data == null && <Alert severity="error" sx={MESSAGE_SX}>Couldn&apos;t load your reports. Please try again later.</Alert>}
      {isLoading && (
        <Box sx={LOADING_SX}>
          <CircularProgress size={20} aria-label="Loading reports" />
        </Box>
      )}
      {data != null && deliverables.length === 0 && <Typography sx={EMPTY_SX}>Reports land here once a workflow runs.</Typography>}
      <List id={listId} dense disablePadding>
        {visibleDeliverables.map((deliverable) => (
          <ListItemButton key={deliverable.deliverableId} onClick={(event) => { event.currentTarget.focus(); onOpen(deliverable.deliverableId) }}>
            <ListItemText primary={reportEntryLabel(deliverable, VIEWER_ZONE)} />
          </ListItemButton>
        ))}
      </List>
      {hiddenCount === 0 && hasNextPage && isFetchNextPageError && (
        <Alert severity="error" sx={MESSAGE_SX} action={<Button size="small" onClick={loadNextPage}>Try again</Button>}>Couldn&apos;t load more reports.</Alert>
      )}
      {hiddenCount === 0 && hasNextPage && !isFetchNextPageError && (
        <SectionMoreButton disabled={isFetchingNextPage} onClick={loadNextPage} testId="chatbox-reports-show-more">
          Show more
        </SectionMoreButton>
      )}
      {hasOverflow && (
        <SectionMoreButton
          ref={toggleRef}
          onClick={toggleShowAll}
          testId="chatbox-reports-show-less"
          disclosure={{ isExpanded: isShowingAll, controlsId: listId }}
        >
          {isShowingAll ? "Show less" : `Show ${hiddenCount} more`}
        </SectionMoreButton>
      )}
    </CollapsibleSection>
  )
}
